import type { CID } from 'multiformats/cid';

import type { LoggerService } from '@/services/abstract/LoggerService';
import type { CidDownloadLogger } from '@/services/abstract/poc/CidDownloadLogger';
import {
  FileStoreService,
  type DagIntegrityResult,
  type GetFileOptions,
} from '@/services/abstract/poc/FileStoreService';
import { parseCid } from '@/services/poc/cidUtils';
import { concatUint8Arrays } from '@/services/poc/dataUtils';
import type { Logger } from '@/types/Logger';
import type { AddFileResult, HeliaNode, ProgressCallback } from '@/types/poc/helia';
import { calculateChunkProgress } from '@/utils/downloadProgress';

// CID codec constants
const RAW_CODEC = 0x55;
const DAG_PB_CODEC = 0x70;
const IDENTITY_HASH_CODE = 0x00;

// Chunk logging interval (log every Nth chunk, matching HAP pattern)
const CHUNK_LOG_INTERVAL = 5;

// Hex formatting for SHA-256
const HEX_RADIX = 16;
const HEX_PAD_LENGTH = 2;
const HEX_PREVIEW_LENGTH = 16;

const sha256Hex = async(data: Uint8Array): Promise<string> => {
  const inputBuffer = new ArrayBuffer(data.byteLength);
  new Uint8Array(inputBuffer).set(data);
  const hashBuffer = await crypto.subtle.digest('SHA-256', inputBuffer);
  const hashArray = new Uint8Array(hashBuffer);
  return Array.from(hashArray)
    .map((b) => b.toString(HEX_RADIX).padStart(HEX_PAD_LENGTH, '0'))
    .join('');
};

interface ChunkLogParams {
  logger: CidDownloadLogger | undefined;
  cidString: string;
  chunkCount: number;
  bytesLoaded: number;
  startTime: number;
  expectedSize: number | undefined;
}

const logChunkProgress = (params: ChunkLogParams): void => {
  const { logger, cidString, chunkCount, bytesLoaded, startTime, expectedSize } = params;
  if (logger === undefined) return;

  const shouldLog = chunkCount === 1 || chunkCount % CHUNK_LOG_INTERVAL === 0;
  if (!shouldLog) return;

  const progress = calculateChunkProgress(bytesLoaded, chunkCount, startTime, expectedSize);
  const step = chunkCount === 1 ? ('fetching' as const) : ('chunk-progress' as const);
  logger.log(cidString, step, { chunks: chunkCount, ...progress });
};

export class FileStoreServiceImpl extends FileStoreService {
  private readonly log: Logger;

  constructor(
    loggerService: LoggerService,
    private readonly downloadLogger?: CidDownloadLogger
  ) {
    super();
    this.log = loggerService.createLogger({ namespace: 'six3ro:services:file-store' });
  }

  // eslint-disable-next-line @typescript-eslint/class-methods-use-this -- implements abstract contract; stateless read
  async verifyDagIntegrity(
    node: HeliaNode,
    cid: CID,
    expectedSize?: number,
    expectedHash?: string
  ): Promise<DagIntegrityResult> {
    try {
      const chunks: Uint8Array[] = [];
      let byteCount = 0;
      for await (const chunk of node.fs.cat(cid)) {
        chunks.push(chunk);
        byteCount += chunk.length;
      }
      if (expectedSize !== undefined && byteCount !== expectedSize) {
        return {
          valid: false,
          error: `Size mismatch: readback ${byteCount} bytes vs expected ${expectedSize}`,
        };
      }
      if (expectedHash !== undefined) {
        const readback = concatUint8Arrays(chunks);
        const readbackHash = await sha256Hex(readback);
        if (readbackHash !== expectedHash) {
          return {
            valid: false,
            error: `Hash mismatch: input SHA-256 ${expectedHash.substring(0, HEX_PREVIEW_LENGTH)}... vs readback ${readbackHash.substring(0, HEX_PREVIEW_LENGTH)}... — fs.addBytes() corrupted the data`,
          };
        }
      }
      return { valid: true, byteCount };
    } catch (error) {
      return {
        valid: false,
        error: error instanceof Error ? error.message : String(error),
      };
    }
  }

  async addFile(
    node: HeliaNode,
    file: File,
    onProgress?: ProgressCallback
  ): Promise<AddFileResult> {
    try {
      const { fs } = node;

      this.log.info(
        `[ADD_FILE] converting file to bytes and adding to local helia blockstore: ${file.name}`
      );
      this.log.debug(`Size: ${file.size} bytes, Type: ${file.type}`);

      const arrayBuffer = await file.arrayBuffer();
      const fileData = new Uint8Array(arrayBuffer);
      this.log.info(`Converted to Uint8Array: ${fileData.length} bytes`);

      if (onProgress !== undefined) {
        onProgress(fileData.length, fileData.length);
      }

      // Hash input bytes before writing — used to detect fs.addBytes() corruption
      const inputHash = await sha256Hex(fileData);
      this.log.info(`Input SHA-256: ${inputHash.substring(0, HEX_PREVIEW_LENGTH)}...`);

      // Use addBytes for raw block storage (no UnixFS wrapping).
      // Creates a simpler CID structure that's easier to retrieve.
      this.log.debug('Calling fs.addBytes()...');
      const cid = await fs.addBytes(fileData);
      this.log.info(`Added file as raw bytes to local blockstore, CID: ${cid.toString()}`);

      // Verify DAG integrity before announcing — catches corrupt writes early.
      const verification = await this.verifyDagIntegrity(node, cid, fileData.length, inputHash);
      if (!verification.valid) {
        this.log.error('DAG integrity check failed', {
          cid: cid.toString(),
          error: verification.error,
        });
        throw new Error(`File write verification failed: ${verification.error}`);
      }
      this.log.info(`DAG integrity verified: ${verification.byteCount} bytes, hash matches`);

      // Pin the file to prevent garbage collection.
      // pins.add() is an AsyncGenerator — must be consumed to drive the DAG walk.
      this.log.debug(`Pinning CID: ${cid.toString()}`);
      // eslint-disable-next-line @typescript-eslint/no-unused-vars -- consuming generator to complete DAG walk
      for await (const _block of node.helia.pins.add(cid)) {
        // consume generator
      }
      this.log.info(`Pinned CID: ${cid.toString()}`);

      let codecName = 'other';
      if (cid.code === RAW_CODEC) codecName = 'raw';
      else if (cid.code === DAG_PB_CODEC) codecName = 'dag-pb';

      this.log.debug('CID Details:', {
        version: cid.version,
        code: cid.code,
        codecName,
        multihash: {
          code: cid.multihash.code,
          size: cid.multihash.size,
        },
        isIdentity: cid.multihash.code === IDENTITY_HASH_CODE,
      });

      return {
        cid,
        path: file.name,
        size: file.size,
      };
    } catch (error) {
      this.log.error('Error adding file to Helia:', error);
      throw error;
    }
  }

  async getFile(
    node: HeliaNode,
    cid: string | CID,
    options?: GetFileOptions
  ): Promise<Uint8Array> {
    const effectiveOptions = options ?? {};
    const { onProgress, expectedSize, signal } = effectiveOptions;
    const downloadLogger = effectiveOptions.downloadLogger ?? this.downloadLogger;

    try {
      const parsedCid = typeof cid === 'string' ? parseCid(cid) : cid;
      this.log.info(`[GET_FILE] Starting retrieval for CID: ${parsedCid.toString()}`);
      this.logCidDetails(parsedCid);

      this.log.debug('Checking blockstore for CID...');
      const hasBlock = await node.helia.blockstore.has(parsedCid);
      this.log.debug(`Blockstore has CID: ${hasBlock}`);

      if (parsedCid.code === RAW_CODEC) {
        return await this.getRawBlock(node, parsedCid, {
          onProgress,
          downloadLogger,
          expectedSize,
          signal,
        });
      }

      return await this.getDagPb(node, parsedCid, {
        onProgress,
        downloadLogger,
        expectedSize,
        signal,
      });
    } catch (error) {
      this.log.error('Error getting file from Helia:', error);
      throw new Error(
        `Failed to retrieve file: ${error instanceof Error ? error.message : String(error)}`
      );
    }
  }

  // eslint-disable-next-line @typescript-eslint/class-methods-use-this -- implements abstract contract; stateless pass-through
  async fileExists(node: HeliaNode, cid: string | CID): Promise<boolean> {
    try {
      const parsedCid = typeof cid === 'string' ? parseCid(cid) : cid;
      await node.fs.stat(parsedCid);
      return true;
    } catch {
      return false;
    }
  }

  // eslint-disable-next-line @typescript-eslint/class-methods-use-this -- implements abstract contract; stateless pass-through
  async pinFile(node: HeliaNode, cid: string | CID): Promise<void> {
    const parsedCid = typeof cid === 'string' ? parseCid(cid) : cid;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- generator must be consumed
    for await (const _block of node.helia.pins.add(parsedCid)) {
      // Consume generator to complete DAG walk.
    }
  }

  // eslint-disable-next-line @typescript-eslint/class-methods-use-this -- implements abstract contract; stateless pass-through
  async unpinFile(node: HeliaNode, cid: string | CID): Promise<void> {
    const parsedCid = typeof cid === 'string' ? parseCid(cid) : cid;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- generator must be consumed
    for await (const _block of node.helia.pins.rm(parsedCid)) {
      // Consume generator to complete DAG walk.
    }
  }

  // eslint-disable-next-line @typescript-eslint/class-methods-use-this -- implements abstract contract; stateless pass-through
  async isPinned(node: HeliaNode, cid: string | CID): Promise<boolean> {
    const parsedCid = typeof cid === 'string' ? parseCid(cid) : cid;
    return await node.helia.pins.isPinned(parsedCid);
  }

  private logCidDetails(parsedCid: CID): void {
    const codecName =
      parsedCid.code === RAW_CODEC
        ? 'raw'
        : parsedCid.code === DAG_PB_CODEC
          ? 'dag-pb'
          : 'other';
    this.log.debug('CID Details:', {
      version: parsedCid.version,
      code: parsedCid.code,
      codecName,
      multihash: {
        code: parsedCid.multihash.code,
        size: parsedCid.multihash.size,
        digest: parsedCid.multihash.digest.length,
      },
      isIdentity: parsedCid.multihash.code === IDENTITY_HASH_CODE,
    });
  }

  private async getDagPb(
    node: HeliaNode,
    parsedCid: CID,
    options: Pick<GetFileOptions, 'onProgress' | 'downloadLogger' | 'expectedSize' | 'signal'>
  ): Promise<Uint8Array> {
    const { onProgress, downloadLogger, expectedSize, signal } = options;

    this.log.debug('CID is dag-pb codec (0x70), reading via fs.cat()...');
    const cidString = parsedCid.toString();
    const chunks: Uint8Array[] = [];
    let bytesLoaded = 0;
    let chunkCount = 0;
    const startTime = Date.now();

    downloadLogger?.log(cidString, 'requesting', { expectedSize });

    try {
      for await (const chunk of node.fs.cat(parsedCid, { signal })) {
        chunkCount += 1;
        chunks.push(chunk);
        bytesLoaded += chunk.length;
        this.log.trace(`Received chunk: ${chunk.length} bytes (total: ${bytesLoaded})`);

        logChunkProgress({
          logger: downloadLogger,
          cidString,
          chunkCount,
          bytesLoaded,
          startTime,
          expectedSize,
        });

        if (onProgress !== undefined) {
          onProgress(bytesLoaded, expectedSize ?? 0);
        }
      }
    } catch (catError) {
      this.log.error('fs.cat() failed:', catError);
      downloadLogger?.log(cidString, 'error', {
        error: catError instanceof Error ? catError.message : 'Unknown error',
        bytesLoaded,
        chunkCount,
      });
      throw catError;
    }

    if (chunks.length === 0) {
      downloadLogger?.log(cidString, 'error', { error: 'No data retrieved from Helia' });
      throw new Error('No data retrieved from Helia');
    }

    const result = concatUint8Arrays(chunks);
    this.log.info(`Retrieved file, total size: ${result.length} bytes`);
    downloadLogger?.log(cidString, 'stored', {
      totalBytes: result.length,
      totalChunks: chunkCount,
      durationMs: Date.now() - startTime,
    });
    return result;
  }

  private async getRawBlock(
    node: HeliaNode,
    parsedCid: CID,
    options: Pick<GetFileOptions, 'onProgress' | 'downloadLogger' | 'expectedSize' | 'signal'>
  ): Promise<Uint8Array> {
    const { onProgress, downloadLogger, expectedSize, signal } = options;
    const { fs, helia } = node;

    this.log.debug('CID is raw codec (0x55), attempting direct blockstore read...');

    try {
      const iterator = helia.blockstore.get(parsedCid);
      const chunks: Uint8Array[] = [];
      for await (const chunk of iterator) {
        chunks.push(chunk);
      }
      const block = chunks.length === 1 ? chunks[0] : concatUint8Arrays(chunks);

      this.log.info(`Retrieved raw block via blockstore.get(), size: ${block.length} bytes`);
      if (onProgress !== undefined) {
        onProgress(block.length, block.length);
      }
      return block;
    } catch (blockError) {
      this.log.warn('blockstore.get() failed, falling back to fs.cat():', blockError);

      try {
        const catChunks: Uint8Array[] = [];
        let catBytesLoaded = 0;
        let catChunkCount = 0;
        const catStartTime = Date.now();
        const rawCidString = parsedCid.toString();

        downloadLogger?.log(rawCidString, 'requesting', { expectedSize, fallback: true });

        for await (const chunk of fs.cat(parsedCid, { signal })) {
          catChunkCount += 1;
          catChunks.push(chunk);
          catBytesLoaded += chunk.length;

          logChunkProgress({
            logger: downloadLogger,
            cidString: rawCidString,
            chunkCount: catChunkCount,
            bytesLoaded: catBytesLoaded,
            startTime: catStartTime,
            expectedSize,
          });
        }
        const catResult = concatUint8Arrays(catChunks);
        this.log.info(
          `Retrieved raw block via fs.cat() fallback, size: ${catResult.length} bytes`
        );

        downloadLogger?.log(rawCidString, 'stored', {
          totalBytes: catResult.length,
          totalChunks: catChunkCount,
          durationMs: Date.now() - catStartTime,
        });

        if (onProgress !== undefined) {
          onProgress(catResult.length, catResult.length);
        }
        return catResult;
      } catch (catError) {
        this.log.error('Both blockstore.get() and fs.cat() failed for raw block:', catError);
        downloadLogger?.log(parsedCid.toString(), 'error', {
          error: catError instanceof Error ? catError.message : 'Unknown error',
          fallback: true,
        });
        throw blockError;
      }
    }
  }
}
